import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  IconButton,
  CircularProgress,
  Divider,
  Typography,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  AccountBalance as AccountIcon,
  Person as PersonIcon,
  Store as BranchIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CreateCustodyDto, UpdateCustodyDto, Custody } from '../../types/custodies.types';
import { custodiesService } from '../../services/custodies.service';
import employeesService from '../../services/employees.service';
import { branchesService } from '../../services/branches.service';
import { chartOfAccountsService, ChartOfAccountDto as ChartOfAccount } from '../../services/chartOfAccounts.service';
import { Employee } from '../../types/employees.types';
import { Branch } from '../../types/branches.types';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

interface CustodyDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  custody?: Custody | null;
}

const translations = {
  en: {
    title: 'New Custody',
    titleEdit: 'Edit Custody',
    basicInfo: 'Basic Information',
    accountingInfo: 'Accounting Integration',
    custodyNumber: 'Custody Number',
    custodyType: 'Custody Type',
    typeBranch: 'Branch Custody',
    typeCentral: 'Central Custody',
    custodyDate: 'Custody Date',
    employee: 'Employee',
    branch: 'Branch',
    account: 'Custody Account',
    initialAmount: 'Initial Amount',
    notes: 'Notes',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading...',
    selectEmployee: 'Please select an employee',
    selectAccount: 'Please select custody account',
    saveFailed: 'Failed to save custody',
    custodyCreated: 'Custody created successfully',
    custodyUpdated: 'Custody updated successfully',
    loadDataError: 'Failed to load data',
    optional: 'Optional',
    required: 'Required',
  },
  ar: {
    title: 'عهدة جديدة',
    titleEdit: 'تعديل عهدة',
    basicInfo: 'المعلومات الأساسية',
    accountingInfo: 'الربط المحاسبي',
    custodyNumber: 'رقم العهدة',
    custodyType: 'نوع العهدة',
    typeBranch: 'عهدة فرع',
    typeCentral: 'عهدة مركزية',
    custodyDate: 'تاريخ العهدة',
    employee: 'الموظف',
    branch: 'الفرع',
    account: 'حساب العهدة',
    initialAmount: 'المبلغ الأولي',
    notes: 'ملاحظات',
    cancel: 'إلغاء',
    save: 'حفظ',
    loading: 'جاري التحميل...',
    selectEmployee: 'يرجى اختيار موظف',
    selectAccount: 'يرجى اختيار حساب العهدة',
    saveFailed: 'فشل حفظ العهدة',
    custodyCreated: 'تم إنشاء العهدة بنجاح',
    custodyUpdated: 'تم تحديث العهدة بنجاح',
    loadDataError: 'فشل تحميل البيانات',
    optional: 'اختياري',
    required: 'مطلوب',
  },
};

const CustodyDialog: React.FC<CustodyDialogProps> = ({ open, onClose, onSave, custody }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState<CreateCustodyDto & { accountId?: number }>({
    custodyNumber: '',
    custodyType: 'Branch',
    custodyDate: new Date().toISOString().split('T')[0],
    employeeId: 0,
    branchId: undefined,
    accountId: undefined,
    initialAmount: 0,
    notes: '',
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { minDate, maxDate } = useFiscalDateRange();

  const resetForm = () => {
    setFormData({
      custodyNumber: '',
      custodyType: 'Branch',
      custodyDate: new Date().toISOString().split('T')[0],
      employeeId: 0,
      branchId: undefined,
      accountId: undefined,
      initialAmount: 0,
      notes: '',
    });
    setSelectedEmployee(null);
    setSelectedBranch(null);
    setSelectedAccount(null);
  };

  useEffect(() => {
    if (open) {
      loadData();
      if (custody) {
        setFormData({
          custodyNumber: custody.custodyNumber,
          custodyType: custody.custodyType || 'Branch',
          custodyDate: custody.custodyDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          employeeId: custody.employeeId || 0,
          branchId: custody.branchId,
          accountId: (custody as any).accountId,
          initialAmount: custody.initialAmount || 0,
          notes: custody.notes || '',
        });
      } else {
        resetForm();
      }
    }
  }, [open, custody]);

  // Load last custody defaults when employee changes (for new custody only)
  useEffect(() => {
    const shouldLoadDefaults = 
      open && 
      !custody && 
      selectedEmployee && 
      branches.length > 0 && 
      accounts.length > 0;

    if (shouldLoadDefaults) {
      loadEmployeeDefaults(selectedEmployee.employeeId);
    }
  }, [open, custody, selectedEmployee, branches, accounts]);

  const loadEmployeeDefaults = async (employeeId: number) => {
    try {
      const defaults = await custodiesService.getEmployeeDefaults(employeeId);
      
      if (defaults) {
        // Set branch if exists
        if (defaults.branchId) {
          const branch = branches.find((b) => b.branchId === defaults.branchId);
          if (branch) {
            setSelectedBranch(branch);
            setFormData((prev) => ({ ...prev, branchId: branch.branchId }));
          }
        }
        // Set account if exists
        if (defaults.accountId) {
          const account = accounts.find((a) => a.accountId === defaults.accountId);
          if (account) {
            setSelectedAccount(account);
            setFormData((prev) => ({ ...prev, accountId: account.accountId }));
          }
        }
      } else {
      }
    } catch (error) {
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [custodyNumber, employeesData, branchesData, accountsData] = await Promise.all([
        custody ? Promise.resolve(custody.custodyNumber) : custodiesService.generateCustodyNumber(),
        employeesService.getAll(),
        branchesService.getAll(),
        chartOfAccountsService.getAll(),
      ]);

      if (!custody) {
        setFormData((prev) => ({ ...prev, custodyNumber: custodyNumber }));
      }

      setEmployees(employeesData.data);
      
      const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
      const userWarehouseIds = JSON.parse(localStorage.getItem('userWarehouseIds') || '[]');
      
      const filteredBranches = userBranchIds.length === 0 && userWarehouseIds.length === 0
        ? branchesData
        : branchesData.filter((b: Branch) => userBranchIds.includes(b.branchId));
      setBranches(filteredBranches);
      
      // فقط حسابات الموجودات المتداولة (Current Assets) أو حسابات خاصة بالعهد
      const custodyAccounts = accountsData.filter(
        acc => acc.isFinalAccount && (acc.accountType === 'Asset' || acc.accountCode?.startsWith('1'))
      );
      setAccounts(custodyAccounts);

      if (custody) {
        const employee = employeesData.data.find((e: Employee) => e.employeeId === custody.employeeId);
        setSelectedEmployee(employee || null);

        if (custody.branchId) {
          const branch = branchesData.find((b: Branch) => b.branchId === custody.branchId);
          setSelectedBranch(branch || null);
        }

        if ((custody as any).accountId) {
          const account = custodyAccounts.find((a: ChartOfAccount) => a.accountId === (custody as any).accountId);
          setSelectedAccount(account || null);
        }
      }
    } catch (error) {
      toast.error(t.loadDataError, { 
        position: 'top-center', 
        autoClose: 3000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      toast.error(t.selectEmployee, { 
        position: 'top-center', 
        autoClose: 3000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
      return;
    }

    if (!selectedAccount) {
      toast.error(t.selectAccount, { 
        position: 'top-center', 
        autoClose: 3000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
      return;
    }

    setSaving(true);

    try {
      const dataToSave = {
        ...formData,
        employeeId: selectedEmployee.employeeId,
        branchId: selectedBranch?.branchId,
        accountId: selectedAccount.accountId,
      };

      if (custody) {
        const updateData: UpdateCustodyDto = {
          custodyId: custody.custodyId,
          custodyDate: dataToSave.custodyDate,
          employeeId: dataToSave.employeeId,
          branchId: dataToSave.branchId,
          accountId: dataToSave.accountId,
          initialAmount: dataToSave.initialAmount,
          notes: dataToSave.notes,
        };
        await custodiesService.update(custody.custodyId, updateData);
        toast.success(t.custodyUpdated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      } else {
        await custodiesService.create(dataToSave as any);
        toast.success(t.custodyCreated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      }

      onSave();
      handleClose();
    } catch (error: any) {
      let errorMessage = t.saveFailed;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data && typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
      
      toast.error(errorMessage, { 
        position: 'top-center', 
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' 
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          fontWeight: 'bold'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon />
          {custody ? t.titleEdit : t.title}
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <>
            {/* Basic Information Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  color: '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <PersonIcon fontSize="small" />
                {t.basicInfo}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Custody Number & Date */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {t.custodyNumber}
                        <Chip 
                          label={t.required} 
                          size="small" 
                          color="error" 
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    value={formData.custodyNumber}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: isDarkMode 
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  />
                  <TextField
                    select
                    fullWidth
                    label={t.custodyType}
                    value={formData.custodyType || 'Branch'}
                    onChange={(e) => setFormData({ ...formData, custodyType: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="Branch">{t.typeBranch}</MenuItem>
                    <MenuItem value="Central">{t.typeCentral}</MenuItem>
                  </TextField>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={t.custodyDate}
                      value={formData.custodyDate ? new Date(formData.custodyDate) : null}
                      onChange={(date) => setFormData({ ...formData, custodyDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                      format="dd/MM/yyyy"
                      views={['year', 'month', 'day']}
                      openTo="day"
                      minDate={minDate}
                      maxDate={maxDate}
                      slotProps={{ textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* Employee */}
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
                  value={selectedEmployee}
                  onChange={(_, newValue) => setSelectedEmployee(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {t.employee}
                          <Chip 
                            label={t.required} 
                            size="small" 
                            color="error" 
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {/* Branch */}
                <Autocomplete
                  options={branches}
                  getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
                  value={selectedBranch}
                  onChange={(_, newValue) => setSelectedBranch(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {t.branch}
                          <Chip 
                            label={t.optional} 
                            size="small" 
                            color="info" 
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.branchId === value.branchId}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {/* Initial Amount */}
                <TextField
                  fullWidth
                  label={
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {t.initialAmount}
                      <Chip 
                        label={t.required} 
                        size="small" 
                        color="error" 
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  type="number"
                  value={formData.initialAmount}
                  onChange={(e) => setFormData({ ...formData, initialAmount: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    inputProps: { min: 0, step: 0.001 }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Accounting Integration Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  color: '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <AccountIcon fontSize="small" />
                {t.accountingInfo}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
                value={selectedAccount}
                onChange={(_, newValue) => setSelectedAccount(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {t.account}
                        <Chip 
                          label={t.required} 
                          size="small" 
                          color="error" 
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>

            {/* Notes */}
            <Box>
              <TextField
                fullWidth
                label={
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {t.notes}
                    <Chip 
                      label={t.optional} 
                      size="small" 
                      color="info" 
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
          gap: 1
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          size="large"
          sx={{
            borderRadius: 2,
            px: 3
          }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
              boxShadow: '0 5px 8px 3px rgba(102, 126, 234, .4)',
            }
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustodyDialog;